import React, { useRef, useEffect, useState } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import * as S from './ColumnDropdown.styles';

export interface ColumnDropdownOption {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  variant?: 'default' | 'danger' | 'success' | 'primary';
  dividerAfter?: boolean;
}

export interface ColumnDropdownSection {
  title?: string;
  options: ColumnDropdownOption[];
}

interface ColumnDropdownProps {
  sections: ColumnDropdownSection[];
  trigger?: React.ReactNode;
  position?: 'left' | 'right';
  placement?: 'top' | 'bottom';
  width?: number;
  dropdownClassName?: string;
}

export const ColumnDropdown: React.FC<ColumnDropdownProps> = ({
  sections,
  trigger = <MoreOutlined style={{ fontSize: '14px', position: 'relative', zIndex: 1 }} />,
  position = 'right',
  placement = 'bottom',
  width = 260,
  dropdownClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Calculate position for fixed positioning when placement is "top"
  useEffect(() => {
    if (isOpen && placement === 'top' && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const newPosition = position === 'left' 
        ? { bottom: window.innerHeight - rect.top + 8, left: rect.left }
        : { bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right };
      setDropdownPosition(newPosition);
    }
  }, [isOpen, placement, position]);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: ColumnDropdownOption) => {
    // Don't do anything if the option is disabled
    if (option.disabled) {
      return;
    }
    
    // Simple approach: first close the dropdown, then call the handler
    setIsOpen(false);

    // Execute after state update
    setTimeout(() => {
      option.onClick();
    }, 0);
  };

  return (
    <S.DropdownContainer ref={dropdownRef}>
      <S.TriggerButton 
        ref={triggerRef}
        onClick={toggleDropdown}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {trigger}
      </S.TriggerButton>
      
      {isOpen && (
        <S.DropdownContent 
          $position={position} 
          $placement={placement} 
          $width={width} 
          $fixedPosition={dropdownPosition}
          className={dropdownClassName}
        >
          {sections.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {section.title && (
                <S.SectionTitle>{section.title}</S.SectionTitle>
              )}
              {section.options.map((option, index) => (
                <React.Fragment key={index}>
                  <S.DropdownItem
                    onClick={(e) => { e.stopPropagation(); handleOptionClick(option); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    disabled={option.disabled}
                    $variant={option.variant}
                    type="button"
                  >
                    {option.icon && (
                      <S.IconWrapper $variant={option.variant}>
                        {option.icon}
                      </S.IconWrapper>
                    )}
                    <S.ItemContent>
                      <S.ItemLabel>{option.label}</S.ItemLabel>
                      {option.description && (
                        <S.ItemDescription>{option.description}</S.ItemDescription>
                      )}
                    </S.ItemContent>
                  </S.DropdownItem>
                  {option.dividerAfter && <S.Divider />}
                </React.Fragment>
              ))}
              {sectionIndex < sections.length - 1 && <S.Divider />}
            </React.Fragment>
          ))}
        </S.DropdownContent>
      )}
    </S.DropdownContainer>
  );
}; 